import * as React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { PercentageOrMoney } from "../../../../../../Shared/PercentageOrMoney/PercentageOrMoney";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../SharedComponents";
import { ChargesHelper } from "../../Helpers/ChargesHelper";
import { AdviserChargesContents, AdviserChargesValidation, ChargeFromMap, ChargeFromTypes } from "../Resource/Contents";
import { ClientAccountStore, ProductDetailsStore } from "../../../../../Stroes/Variable";
import { SourceType } from "../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { WizardLinter, EWizardLinterStatus } from "../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../NewBusinessWizard/Resource/EWizardLinterHelper";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { Action } from "../../../../../Actions/Action";

interface IIACProps extends IProcessComponentProps {
    type: SubAccountType;
    existingAccount?: boolean;
}

interface IIACState {
    wrapcashCurrentTotal?: number;
}

export class IAC extends ProcessComponent<IIACProps, IIACState> {
    constructor(props: any) {
        super(props);
        this.state = {
            wrapcashCurrentTotal: null,
        };
        this.subscribeToStore(ProductDetailsStore, () => this.onProductDetailsChange());
    }

    componentDidMount() {
        super.componentDidMount();
        const { data, onChange, existingAccount, type } = this.props;
        if (existingAccount && !this.state.wrapcashCurrentTotal) {
            this.onProductDetailsChange();
        }
        const sourceType = SubAccountTypeHelper.isOnPlatformPension(type) ? SourceType.ProductCash : SourceType.WrapCash;
        if (isNullOrEmpty(data?.InitialAdviserCharge?.SourceType) || data?.InitialAdviserCharge?.SourceType == 0) {
            this.onDataChange(onChange, { SourceType: sourceType }, data, "InitialAdviserCharge");
        }
        if (!this.state.wrapcashCurrentTotal) {
            const clientAccount = ClientAccountStore.getClientAccountInfo();
            if (clientAccount?.SubAccounts?.some((x) => x.SubAccountType == SubAccountType.WrapCash)) {
                const _date = JsonDateParser.getFormatDateString(new Date(), "yyyy/MM/dd") as any;
                Action.getProductsValuationDetailSummary(
                    clientAccount?.SubAccounts?.find((a) => a.SubAccountType == SubAccountType.WrapCash).HierarchyId,
                    null,
                    _date
                );
            }
        }
    }

    onProductDetailsChange() {
        const result = ProductDetailsStore.getSubAccount(SubAccountType.WrapCash);
        if (result) {
            this.setState({
                wrapcashCurrentTotal: result.TotalValue?.Amount,
            });
        }
    }

    isPercentage(): boolean {
        const { data } = this.props;
        return (
            !data || (data.InitialAdviserCharge && data.InitialAdviserCharge.Type == MoneyOrPercentageType.Percentage)
        );
    }

    renderAlert(totalForEstimation: number) {
        const percentage = this.getPercentageBasedOnAmountOrPercentage(totalForEstimation);
        const alerts = [];
        if (this.iacGiven() && SubAccountTypeHelper.isOnPlatformPension(this.props.type)) {
            alerts.push(<Alert alertType={AlertType.Warning} message={AdviserChargesValidation.IACWarningMessage} />);
        }
        if (percentage >= 10) {
            alerts.push(
                <Alert
                    alertType={AlertType.Warning}
                    message={`You have requested an adviser payment of ${percentage.toFixed(
                        2
                    )}%. Please continue if this is correct.`}
                />
            );
        }
        if (
            (this.props.data?.InitialAdviserCharge?.SourceType == undefined ||
                this.props.data?.InitialAdviserCharge?.SourceType == SourceType.NotSpecified ||
                this.props.data?.InitialAdviserCharge?.SourceType == SourceType.WrapCash) &&
            this.iacGiven()
        ) {
            alerts.push(
                <Alert alertType={AlertType.Warning} message={AdviserChargesValidation.WrapCashIACWarningMessage} />
            );
        }
        if (!this.validateSufficientWrapCashForSPT(totalForEstimation)) {
            alerts.push(
                <Alert alertType={AlertType.Warning} message={AdviserChargesValidation.InsufficientCashForIAC} />
            );
        }
        if (alerts.length > 0) {
            return <>{alerts}</>;
        }
    }

    validateSufficientWrapCashForSPT(totalForEstimation: number) {
        if (!this.state.wrapcashCurrentTotal || !this.iacGiven()) {
            return true;
        }
        const { data } = this.props;
        let iac = 0;
        if (
            data?.InitialAdviserCharge?.Type === MoneyOrPercentageType.Money &&
            data?.InitialAdviserCharge?.Money.Amount > 0
        ) {
            iac = data.InitialAdviserCharge.Money.Amount;
        } else if (
            data?.InitialAdviserCharge?.Type === MoneyOrPercentageType.Percentage &&
            data?.InitialAdviserCharge?.Percentage > 0
        ) {
            iac = (totalForEstimation * data.InitialAdviserCharge.Percentage) / 100;
        }
        return iac <= this.state.wrapcashCurrentTotal;
    }

    getPercentageBasedOnAmountOrPercentage(totalForEstimation: number) {
        const iac = this.props.data?.InitialAdviserCharge;
        let percentageValue = iac?.Percentage;
        if (iac && iac.Money && iac.Money.Amount && iac.Money.Amount > 0) {
            percentageValue = (iac.Money.Amount / totalForEstimation) * 100;
        }
        return percentageValue;
    }

    getTotalForEstimation(): number {
        return ChargesHelper.getTotalForIACEstimation(this.props.data, this.props.type);
    }

    iacGiven(): boolean {
        const amount = this.props.data?.InitialAdviserCharge?.Money;
        const percentage = this.props.data?.InitialAdviserCharge?.Percentage;
        return percentage || (amount && amount.Amount && amount.Amount > 0);
    }

    public render(): JSX.Element {
        const { data, onChange, type } = this.props;
        const totalForEstimation = this.getTotalForEstimation();
        if (totalForEstimation <= 0) {
            return null;
        }
        return (
            <WizardLinter status={EWizardLinterStatus.PleaseCheck} enabled={EWizardLinterHelper.shouldDisplay(type)}>
                {SectionHeader(
                    AdviserChargesContents.IAC,
                    "For monetary (£) Initial Adviser Charges – we try to take these straight away if there is cleared cash. For percentage (%) Initial Adviser Charges – we will pay this once the expected cash has been applied."
                )}
                {this.renderAlert(totalForEstimation)}
                <PercentageOrMoney
                    label={"Rate"}
                    data={data?.InitialAdviserCharge}
                    //tooltip={AdviserChargesContents.IACTooltip}
                    onChange={(v) => {
                        this.onDataChange(onChange, v, data, "InitialAdviserCharge");
                    }}
                    totalForEstimation={totalForEstimation}
                    validationRules={[
                        {
                            errorMessage: AdviserChargesValidation.InsufficientCashForIAC,
                            validator: (v) => {
                                return true;
                            },
                        },
                    ]}
                    onRef={(ref) => this.appendChild(ref)}
                    cssWrapper="mb-4"
                    cssPercentageOrMoneyClasses="control-size-115"
                />

                {SubAccountTypeHelper.isOnPlatformPension(type) && (
                    <RadiosEditor
                        cssWrapper="rg-150 mb-24 mt-30"
                        label={"Take the charge from:"}
                        options={ChargeFromTypes}
                        selected={data?.InitialAdviserCharge?.SourceType}
                        getText={(o) => ChargeFromMap[o]}
                        required={this.iacGiven() ? "required" : null}
                        onChange={(v) => this.onDataChange(onChange, { SourceType: v }, data, "InitialAdviserCharge")}
                    />
                )}
                <hr className="mb-30" />
            </WizardLinter>
        );
    }
}
